// @radish/core/billing (ops plan 2026-10-13-billing-system §4.2): what Radish bills a location each month, and which
// features the location may use. Plans are data: a plan version says, for each feature, whether it is included, sold a
// la carte at a price, or not offered, and for each device class its first-unit and each-additional price. Pure: no
// clock, no Stripe. Money is bigint minor units, rates parts per 10^8; every rounding is a named step.
//
// Andrew's calls (plan Decided 5 and 6, 2026-10-13): monthly only at launch (A57); a price staff haven't filled in stays
// blank, and a version with a blank price can't be published (A56); a 14-day grace after a failed card with warnings on
// days 0, 3, 7 and 12, after which only a la carte features pause (A59); staff grant free time of any length and
// discounts, a reseller at most 30 days of trial or free period on its own accounts and no discounts (A60).
import { RATE_SCALE, rate, type Currency, type Money, type Rate } from '@radish/primitives';
import { add, applyRate, compare, isNegative, multiply, rateToLegacy, subtract, sum, zero } from '../money/index.js';
import { err, ok, type Result } from '../result.js';
import { addDays, localDateToDays, type LocalDate } from '../time/index.js';
import { civilFromDays, daysFromCivil, daysInMonth, formatLocalDate } from '../time/calendar.js';

// ---- the catalog ---------------------------------------------------------------------------------------------------

/**
 * Features that are never entitlement-checked: a location sells, takes payments, runs its KDS, prints and reads its
 * reports whatever its plan, its card or its connection (plan §4.5). A plan always includes them.
 */
export const NEVER_GATED_FEATURES = ['selling', 'payments', 'kds', 'printing', 'reports'] as const;

export const isNeverGated = (featureKey: string): boolean => (NEVER_GATED_FEATURES as readonly string[]).includes(featureKey);

/** How a plan version offers one feature. */
export type FeatureInclusion = 'included' | 'a_la_carte' | 'not_offered';

export interface FeatureTerms {
  readonly inclusion: FeatureInclusion;
  /** The monthly price of an a la carte feature; null while staff haven't set it. Ignored unless a la carte. */
  readonly price: Money | null;
}

/** The kinds of unit a location is billed for. */
export const DEVICE_CLASS_KEYS = ['register', 'handheld', 'kds', 'customer_display', 'menu_display', 'kitchen_printer', 'location_node'] as const;
export type DeviceClassKey = (typeof DEVICE_CLASS_KEYS)[number];

/** Where a device class's units are counted from. */
export type CountedFrom =
  | { readonly source: 'device'; readonly formFactor: string }
  | { readonly source: 'kitchen_printer' }
  | { readonly source: 'location_node' };

export interface DeviceClass {
  readonly key: DeviceClassKey;
  readonly countedFrom: CountedFrom;
}

export interface DeviceTerms {
  /** The first unit's monthly price; null while unset. */
  readonly firstUnit: Money | null;
  /** Each further unit's monthly price; null while unset. */
  readonly eachAdditional: Money | null;
}

/** Billing intervals the model knows. Only `month` is sold at launch (A57); `year` is a later data change. */
export type BillingInterval = 'month' | 'year';
export const SOLD_INTERVALS: readonly BillingInterval[] = ['month'];

/** What one plan version charges. A published version is never edited; saving a draft makes the next version. */
export interface PlanTerms {
  readonly currency: Currency;
  readonly interval: BillingInterval;
  /** The plan's own monthly price before features and devices; null while unset. */
  readonly basePrice: Money | null;
  readonly features: Readonly<Record<string, FeatureTerms>>;
  readonly devices: Readonly<Partial<Record<DeviceClassKey, DeviceTerms>>>;
}

/** One reason a version can't be published, at the path of the price or term that's wrong. */
export interface PublishProblem {
  readonly path: string;
  readonly problem: 'price_blank' | 'price_negative' | 'currency_mismatch' | 'interval_not_sold' | 'never_gated_feature_not_included' | 'unknown_feature';
}

/**
 * Why a version can't be published, or none. Every price a location could be charged must be filled in (A56: blanks
 * stay blank until staff set them, and no location is put on a plan with one), never negative, in the plan's currency.
 */
export function publishProblems(terms: PlanTerms, featureKeys: readonly string[]): PublishProblem[] {
  const problems: PublishProblem[] = [];
  const price = (path: string, value: Money | null): void => {
    if (value === null) problems.push({ path, problem: 'price_blank' });
    else if (isNegative(value)) problems.push({ path, problem: 'price_negative' });
    else if (value.currency !== terms.currency) problems.push({ path, problem: 'currency_mismatch' });
  };
  if (!SOLD_INTERVALS.includes(terms.interval)) problems.push({ path: 'interval', problem: 'interval_not_sold' });
  price('basePrice', terms.basePrice);
  for (const key of NEVER_GATED_FEATURES) {
    if (terms.features[key]?.inclusion !== 'included') problems.push({ path: `features.${key}`, problem: 'never_gated_feature_not_included' });
  }
  for (const [key, f] of Object.entries(terms.features)) {
    if (!featureKeys.includes(key)) problems.push({ path: `features.${key}`, problem: 'unknown_feature' });
    else if (f.inclusion === 'a_la_carte') price(`features.${key}.price`, f.price);
  }
  for (const key of DEVICE_CLASS_KEYS) {
    const d = terms.devices[key];
    if (d === undefined) continue;
    price(`devices.${key}.firstUnit`, d.firstUnit);
    price(`devices.${key}.eachAdditional`, d.eachAdditional);
  }
  return problems;
}

// ---- quantities ----------------------------------------------------------------------------------------------------

export interface FleetDevice {
  readonly formFactor: string;
  /** Legacy `is_billing`: set by staff only. */
  readonly billed: boolean;
  /** False once the device is deleted or retired. */
  readonly live: boolean;
}

export interface BillableFleet {
  readonly devices: readonly FleetDevice[];
  /** Printers in Hardware with a kitchen role. */
  readonly kitchenPrinters: number;
  readonly locationNodes: number;
}

/** Per device class, the units counted: live devices with Billed on of that form factor, kitchen printers, nodes. */
export function billableQuantities(fleet: BillableFleet, classes: readonly DeviceClass[]): Partial<Record<DeviceClassKey, number>> {
  const out: Partial<Record<DeviceClassKey, number>> = {};
  for (const c of classes) {
    const from = c.countedFrom;
    out[c.key] =
      from.source === 'device'
        ? fleet.devices.filter((d) => d.live && d.billed && d.formFactor === from.formFactor).length
        : from.source === 'kitchen_printer'
          ? fleet.kitchenPrinters
          : fleet.locationNodes;
  }
  return out;
}

// ---- the bill's lines ----------------------------------------------------------------------------------------------

export type LineKey = 'base' | `feature:${string}` | `device:${DeviceClassKey}`;

export interface BillLine {
  readonly lineKey: LineKey;
  readonly quantity: number;
  readonly amount: Money;
}

export type PriceError =
  | { readonly code: 'price_blank'; readonly path: string }
  | { readonly code: 'feature_not_offered'; readonly featureKey: string };

/** A device class's monthly charge for `n` units: the first unit plus (n − 1) each additional. Zero units cost nothing. */
export function deviceCharge(terms: DeviceTerms, units: number, currency: Currency): Result<Money, 'price_blank'> {
  if (units <= 0) return ok(zero(currency));
  if (terms.firstUnit === null || (units > 1 && terms.eachAdditional === null)) return err('price_blank');
  return ok(units > 1 && terms.eachAdditional !== null ? add(terms.firstUnit, multiply(terms.eachAdditional, BigInt(units - 1))) : terms.firstUnit);
}

/**
 * The bill's lines, before adjustments: the base, each included feature at $0, each a la carte feature bought at its
 * price, then each device class the version prices. The KDS rule (the first KDS is not free, Jarrett) and the free
 * first register are the version's data, not code.
 */
export function priceLines(terms: PlanTerms, purchased: readonly string[], quantities: Partial<Record<DeviceClassKey, number>>): Result<BillLine[], PriceError> {
  if (terms.basePrice === null) return err({ code: 'price_blank', path: 'basePrice' });
  const lines: BillLine[] = [{ lineKey: 'base', quantity: 1, amount: terms.basePrice }];
  for (const key of purchased) {
    const f = terms.features[key];
    if (f === undefined || f.inclusion === 'not_offered') return err({ code: 'feature_not_offered', featureKey: key });
  }
  for (const [key, f] of Object.entries(terms.features)) {
    if (f.inclusion === 'included') lines.push({ lineKey: `feature:${key}`, quantity: 1, amount: zero(terms.currency) });
    else if (f.inclusion === 'a_la_carte' && purchased.includes(key)) {
      if (f.price === null) return err({ code: 'price_blank', path: `features.${key}.price` });
      lines.push({ lineKey: `feature:${key}`, quantity: 1, amount: f.price });
    }
  }
  for (const key of DEVICE_CLASS_KEYS) {
    const d = terms.devices[key];
    if (d === undefined) continue;
    const units = quantities[key] ?? 0;
    const charge = deviceCharge(d, units, terms.currency);
    if (!charge.ok) return err({ code: 'price_blank', path: `devices.${key}` });
    lines.push({ lineKey: `device:${key}`, quantity: units, amount: charge.value });
  }
  return ok(lines);
}

// ---- adjustments ---------------------------------------------------------------------------------------------------

export type AdjustmentKind = 'trial' | 'free_period' | 'discount';

export type AdjustmentAmount = { readonly percent: Rate } | { readonly fixed: Money };

export interface Adjustment {
  readonly adjustmentId: string;
  readonly adjustmentKind: AdjustmentKind;
  /** The whole bill, or one line of it. Trials and free periods always cover the whole bill. */
  readonly lineKey: LineKey | null;
  /** A discount's amount off; null for a trial or free period. */
  readonly amount: AdjustmentAmount | null;
  /** The first local date it covers. */
  readonly startsOn: LocalDate;
  /** The first local date it no longer covers (exclusive); null for a discount with no end. */
  readonly endsOn: LocalDate | null;
}

/** Whether an adjustment covers a billing period that starts on `periodStart`. */
export const covers = (a: Adjustment, periodStart: LocalDate): boolean =>
  a.startsOn <= periodStart && (a.endsOn === null || periodStart < a.endsOn);

export interface AdjustedLine extends BillLine {
  readonly discount: Money;
  readonly net: Money;
}

export interface AdjustedBill {
  readonly lines: readonly AdjustedLine[];
  readonly subtotal: Money;
  /** Line discounts plus the whole-bill discount. */
  readonly discountTotal: Money;
  readonly total: Money;
  /** The trial or free period that zeroed the period, if one did. */
  readonly freeBy: string | null;
}

const minMoney = (a: Money, b: Money): Money => (compare(a, b) <= 0 ? a : b);

/** One discount's amount off `base`: a percentage rounded half-up to the cent, or a fixed amount, never more than base. */
function discountOff(amount: AdjustmentAmount, base: Money): Money {
  // rounding-step: billing.discount.percent (half-up to the cent, once per discount)
  const off = 'percent' in amount ? applyRate(base, amount.percent, 'HALF_UP') : amount.fixed;
  return isNegative(off) ? zero(base.currency) : minMoney(off, base);
}

/**
 * The bill for the period starting `periodStart` after its adjustments. A trial or free period covering the period
 * zeroes it. Otherwise line discounts come off their line first, then whole-bill discounts come off what is left, each
 * rounded once, half-up to the cent. Nothing goes below zero.
 */
export function applyAdjustments(lines: readonly BillLine[], adjustments: readonly Adjustment[], periodStart: LocalDate, currency: Currency): AdjustedBill {
  const active = adjustments.filter((a) => covers(a, periodStart));
  const subtotal = sum(lines.map((l) => l.amount), currency);
  const free = active.find((a) => a.adjustmentKind === 'trial' || a.adjustmentKind === 'free_period');
  if (free !== undefined) {
    return {
      lines: lines.map((l) => ({ ...l, discount: l.amount, net: zero(currency) })),
      subtotal, discountTotal: subtotal, total: zero(currency), freeBy: free.adjustmentId,
    };
  }
  const discounts = active.filter((a) => a.adjustmentKind === 'discount' && a.amount !== null);
  const adjusted = lines.map((l) => {
    let net = l.amount;
    for (const d of discounts.filter((x) => x.lineKey === l.lineKey)) if (d.amount !== null) net = subtract(net, discountOff(d.amount, net));
    return { ...l, discount: subtract(l.amount, net), net };
  });
  let total = sum(adjusted.map((l) => l.net), currency);
  for (const d of discounts.filter((x) => x.lineKey === null)) if (d.amount !== null) total = subtract(total, discountOff(d.amount, total));
  return { lines: adjusted, subtotal, discountTotal: subtract(subtotal, total), total, freeBy: null };
}

// ---- dates and the estimate ----------------------------------------------------------------------------------------

/** `date` plus `months` calendar months, on the same day of the month or the month's last day (Stripe's anchor rule). */
export function addMonthsClamped(date: LocalDate, months: number): LocalDate {
  const { year, month, day } = civilFromDays(localDateToDays(date));
  const index = year * 12 + (month - 1) + months;
  const y = Math.floor(index / 12);
  const m = index - y * 12 + 1;
  return formatLocalDate(daysFromCivil(y, m, Math.min(day, daysInMonth(y, m))));
}

export interface EstimateInput {
  readonly terms: PlanTerms;
  readonly purchased: readonly string[];
  readonly quantities: Partial<Record<DeviceClassKey, number>>;
  readonly adjustments: readonly Adjustment[];
  /** Today, in the location's time zone. */
  readonly today: LocalDate;
  /**
   * The subscription's billing anchor (its first billing date), or null for a location not yet on the plan: it is
   * then the end of a trial or free period starting today, or today.
   */
  readonly anchor: LocalDate | null;
  /**
   * B1920: the end of the subscription's current Stripe period, as a date in the location's zone (null or absent before
   * the first sync): the upcoming invoice's date (`upcomingInvoiceOn`).
   */
  readonly periodEndsOn?: LocalDate | null;
}

export interface Estimate {
  /** The plan's monthly price before adjustments. */
  readonly monthly: Money;
  /** What is charged on starting now: the first period, or nothing while a trial or free period runs. */
  readonly dueNow: Money;
  /** The next invoice's date and its bill. */
  readonly nextBillingDate: LocalDate;
  readonly nextBill: AdjustedBill;
  readonly lines: readonly BillLine[];
}

/** The latest end of a trial or free period that covers `today` (its end is the first billing date), or null. */
export function freeUntil(adjustments: readonly Adjustment[], today: LocalDate): LocalDate | null {
  const ends = adjustments
    .flatMap((a) => ((a.adjustmentKind === 'trial' || a.adjustmentKind === 'free_period') && covers(a, today) && a.endsOn !== null ? [a.endsOn] : []))
    .sort();
  return ends.at(-1) ?? null;
}

/** The first anchor-day date on or after `today`. */
export function nextBillingDate(anchor: LocalDate, today: LocalDate): LocalDate {
  if (today <= anchor) return anchor;
  let n = 1;
  while (addMonthsClamped(anchor, n) < today) n += 1;
  return addMonthsClamped(anchor, n);
}

/**
 * B1920: the date of the subscription's upcoming invoice, legacy's `auto_pay_on` (Stripe's upcoming invoice,
 * GetLocationSubscription.php:36), which Plans & Billing shows as the Next Billing Date and whose discounts the Stripe
 * sync sets. Off a billing day it is the next billing day (`nextBillingDate`). On one, Stripe makes that day's invoice at
 * its anchor's instant: the start of the day only for a subscription that began with free time (a `trial_end`); one put
 * on a plan with no free time, or whose free time ended early, renews at the time of day it started. So the billing day
 * itself stays the upcoming invoice until Stripe's own period (`current_period_end`, stored by the sync, as a date in
 * the location's zone: `periodEndsOn`) has moved on to the next billing day. A stored period that is not one of the
 * anchor's billing days (the trial period of free time just ended, #2974 review B1) or is missing says nothing, and the
 * billing day counts as not yet billed.
 */
export function upcomingInvoiceOn(anchor: LocalDate, today: LocalDate, periodEndsOn: LocalDate | null): LocalDate {
  const due = nextBillingDate(anchor, today);
  if (due !== today || periodEndsOn === null) return due;
  let n = 1;
  while (addMonthsClamped(anchor, n) <= today) n += 1;
  const following = addMonthsClamped(anchor, n);
  return periodEndsOn === following ? following : due;
}

/** The monthly price, what is due now, and the next billing date and bill, all in the location's time zone. */
export function estimate(input: EstimateInput): Result<Estimate, PriceError> {
  const priced = priceLines(input.terms, input.purchased, input.quantities);
  if (!priced.ok) return priced;
  const lines = priced.value;
  const currency = input.terms.currency;
  const monthly = sum(lines.map((l) => l.amount), currency);
  if (input.anchor === null) {
    const freeEnd = freeUntil(input.adjustments, input.today);
    if (freeEnd !== null) {
      return ok({ monthly, dueNow: zero(currency), nextBillingDate: freeEnd, nextBill: applyAdjustments(lines, input.adjustments, freeEnd, currency), lines });
    }
    const first = applyAdjustments(lines, input.adjustments, input.today, currency);
    const next = addMonthsClamped(input.today, 1);
    return ok({ monthly, dueNow: first.total, nextBillingDate: next, nextBill: applyAdjustments(lines, input.adjustments, next, currency), lines });
  }
  const next = upcomingInvoiceOn(input.anchor, input.today, input.periodEndsOn ?? null);
  return ok({ monthly, dueNow: zero(currency), nextBillingDate: next, nextBill: applyAdjustments(lines, input.adjustments, next, currency), lines });
}

// ---- who may grant what (A60) --------------------------------------------------------------------------------------

/** The longest trial or free period a reseller may grant, in days (A60). */
export const RESELLER_MAX_FREE_DAYS = 30;

export interface Grantor {
  readonly via: 'radish_staff' | 'reseller';
  /** For a reseller: the account belongs to its own reseller. */
  readonly ownsAccount: boolean;
}

export type GrantRefusal =
  | 'not_own_account' | 'reseller_no_discounts' | 'reseller_over_limit' | 'needs_end_date' | 'ends_before_start' | 'needs_amount' | 'percent_over_100'
  | 'percent_too_precise';

/**
 * A discount's percentage as the billing processor takes it: '12.34', '10'. It takes two decimals at most, so a finer
 * percentage is refused (`percent_too_precise`) rather than billed differently from our estimate.
 */
export const percentOffText = (r: Rate): string => rateToLegacy(r, 'percent');
/** A discount percentage typed with two decimals, in hundredths of a percent (12.34 % is 1234), as a Rate. */
export const percentFromHundredths = (hundredths: bigint): Rate => rate(hundredths * 10_000n);
const TWO_DECIMALS = /^\d+(?:\.\d{1,2})?$/;

/** Whether a grantor may grant an adjustment: staff any length, a reseller only trials or free periods of 30 days at most on its own accounts. */
export function mayGrant(grantor: Grantor, a: Adjustment): Result<true, GrantRefusal> {
  if (a.adjustmentKind !== 'discount' && a.endsOn === null) return err('needs_end_date');
  if (a.endsOn !== null && a.endsOn <= a.startsOn) return err('ends_before_start');
  if (a.adjustmentKind === 'discount') {
    if (a.amount === null) return err('needs_amount');
    if ('percent' in a.amount && a.amount.percent.scaled > RATE_SCALE) return err('percent_over_100');
    if ('percent' in a.amount && !TWO_DECIMALS.test(percentOffText(a.amount.percent))) return err('percent_too_precise');
  }
  if (grantor.via === 'radish_staff') return ok(true);
  if (!grantor.ownsAccount) return err('not_own_account');
  if (a.adjustmentKind === 'discount') return err('reseller_no_discounts');
  if (a.endsOn === null || localDateToDays(a.endsOn) - localDateToDays(a.startsOn) > RESELLER_MAX_FREE_DAYS) return err('reseller_over_limit');
  return ok(true);
}

// ---- a failed card (A59) -------------------------------------------------------------------------------------------

/** Days of grace after a failed card before a la carte features pause (A59). */
export const DEFAULT_GRACE_DAYS = 14;

/** The grace days the merchant is warned on: the day it fails, days 3 and 7, and two days before it ends (A59). */
export function warningDays(graceDays: number): number[] {
  return [...new Set([0, 3, 7, graceDays - 2])].filter((d) => d >= 0 && d < graceDays).sort((a, b) => a - b);
}

export type DunningState =
  | { readonly state: 'ok' }
  | { readonly state: 'grace'; readonly day: number; readonly of: number; readonly warnToday: boolean; readonly endsOn: LocalDate }
  | { readonly state: 'grace_ended'; readonly endedOn: LocalDate };

/** Where a location stands after a failed card first failed on `failedOn` (null: nothing unpaid), in its own dates. */
export function dunningState(failedOn: LocalDate | null, graceDays: number, today: LocalDate): DunningState {
  if (failedOn === null) return { state: 'ok' };
  const day = localDateToDays(today) - localDateToDays(failedOn);
  const endsOn = addDays(failedOn, graceDays);
  if (day >= graceDays) return { state: 'grace_ended', endedOn: endsOn };
  return { state: 'grace', day: Math.max(day, 0), of: graceDays, warnToday: warningDays(graceDays).includes(day), endsOn };
}

/** The notices a failed card sends the merchant (drafted for Andrew's review; sent only once he approves them). */
export type DunningNotice = 'payment_failed' | 'payment_reminder' | 'grace_ending' | 'grace_ended';

/**
 * The notice due today, or null: the failure itself on day 0, a reminder on days 3 and 7, "add-ons pause in two days"
 * on day N − 2, and "add-ons paused" once grace has ended (A59).
 */
export function dunningNotice(d: DunningState): DunningNotice | null {
  if (d.state === 'grace_ended') return 'grace_ended';
  if (d.state !== 'grace' || !d.warnToday) return null;
  if (d.day === 0) return 'payment_failed';
  return d.day === d.of - 2 ? 'grace_ending' : 'payment_reminder';
}

// ---- entitlements --------------------------------------------------------------------------------------------------

export interface EntitlementInput {
  /** Off for every location on "Legacy, everything included" until staff put it on a real plan (Decided 5). */
  readonly enforcement: 'off' | 'on';
  readonly terms: PlanTerms;
  readonly purchased: readonly string[];
  readonly dunning: DunningState;
}

export type Entitlements = { readonly everything: true } | { readonly everything: false; readonly features: readonly string[] };

/**
 * The features a location may use. Enforcement off grants everything. On, it grants the never-gated features, the
 * plan's included ones, and the a la carte ones bought, which pause (and only they) once grace has ended.
 */
export function entitlements(input: EntitlementInput): Entitlements {
  if (input.enforcement === 'off') return { everything: true };
  const included = Object.entries(input.terms.features).filter(([, f]) => f.inclusion === 'included').map(([k]) => k);
  const bought =
    input.dunning.state === 'grace_ended'
      ? []
      : input.purchased.filter((k) => input.terms.features[k]?.inclusion === 'a_la_carte');
  return { everything: false, features: [...new Set([...NEVER_GATED_FEATURES, ...included, ...bought])].sort() };
}

/** The a la carte features bought that are paused now (grace has ended), never a never-gated or included one. */
export function pausedFeatures(terms: PlanTerms, purchased: readonly string[], dunning: DunningState): string[] {
  if (dunning.state !== 'grace_ended') return [];
  return [...new Set(purchased)].filter((k) => terms.features[k]?.inclusion === 'a_la_carte' && !isNeverGated(k)).sort();
}

/** Whether a feature is on. Never-gated features are always on, whatever the input. */
export const isEntitled = (e: Entitlements, featureKey: string): boolean =>
  isNeverGated(featureKey) || e.everything || e.features.includes(featureKey);

// ---- what a location holds of its entitlements (slice 6) -----------------------------------------------------------

/** The built-in plan every migrated location starts on (Decided 5): every feature on, enforcement off. */
export const LEGACY_PLAN_NAME = 'Legacy, everything included';

/** An a la carte feature the location could add, with the plan's monthly price: what its upsell page offers. */
export interface EntitlementOffer {
  readonly featureKey: string;
  readonly name: string;
  readonly price: Money;
}

/**
 * The entitlements as the cloud writes them for a location (config entity `billing.entitlements`) and as the node, its
 * registers and the back office read them, offline too. With enforcement off `features` is empty and means nothing.
 */
export interface EntitlementFacts {
  readonly planName: string;
  readonly enforcement: 'off' | 'on';
  /** The features on, when enforced (core `entitlements`): never-gated, included, and bought (not paused). */
  readonly features: readonly string[];
  /** The a la carte features bought that grace has paused. */
  readonly paused: readonly string[];
  /** The a la carte features not on, each with its monthly price, for the upsell. */
  readonly offers: readonly EntitlementOffer[];
}

/** A location on Legacy: everything on, nothing offered. */
export const LEGACY_ENTITLEMENTS: EntitlementFacts = { planName: LEGACY_PLAN_NAME, enforcement: 'off', features: [], paused: [], offers: [] };

export interface EntitlementFactsInput extends EntitlementInput {
  readonly planName: string;
  /** Each feature's name, for the upsell. */
  readonly featureNames: Readonly<Record<string, string>>;
}

/** The facts for a location on a plan: core's entitlements, the paused add-ons, and an offer per a la carte feature not on. */
export function entitlementFacts(input: EntitlementFactsInput): EntitlementFacts {
  if (input.enforcement === 'off') return { ...LEGACY_ENTITLEMENTS, planName: input.planName };
  const on = entitlements(input);
  const features = on.everything ? [] : on.features;
  const offers = Object.entries(input.terms.features)
    .filter(([k, f]) => f.inclusion === 'a_la_carte' && f.price !== null && !isNeverGated(k) && !features.includes(k))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, f]) => ({ featureKey: k, name: input.featureNames[k] ?? k, price: f.price as Money }));
  return { planName: input.planName, enforcement: 'on', features, paused: pausedFeatures(input.terms, input.purchased, input.dunning), offers };
}

/** How a feature shows at a location: `on`, or `upsell` (not on its plan, or paused after grace). */
export type FeatureStanding = 'on' | 'upsell';

/**
 * Whether a location may use a feature, from the entitlements it last synced. It fails open: with nothing synced (an
 * older build, a new device, no connection yet) everything is on, and a never-gated feature (selling, payments, the
 * KDS, printing, reports) is on whatever the facts say. Only facts with enforcement on can turn anything off.
 */
export function featureStanding(facts: EntitlementFacts | null, featureKey: string): FeatureStanding {
  if (facts === null || isNeverGated(featureKey) || facts.enforcement !== 'on') return 'on';
  return facts.features.includes(featureKey) ? 'on' : 'upsell';
}

/** The plan's offer for a feature that is not on, or null (nothing to buy: not sold on this plan, or already on). */
export function offerFor(facts: EntitlementFacts | null, featureKey: string): EntitlementOffer | null {
  if (featureStanding(facts, featureKey) === 'on') return null;
  return facts?.offers.find((o) => o.featureKey === featureKey) ?? null;
}

/** Whether two sets of facts say the same (the cloud writes a new fact only when they differ). */
export function sameEntitlements(a: EntitlementFacts, b: EntitlementFacts): boolean {
  const offers = (f: EntitlementFacts) => f.offers.map((o) => `${o.featureKey}|${o.name}|${o.price.minor.toString()}|${o.price.currency}`).join(',');
  return a.planName === b.planName && a.enforcement === b.enforcement && a.features.join(',') === b.features.join(',')
    && a.paused.join(',') === b.paused.join(',') && offers(a) === offers(b);
}

// ---- versions ------------------------------------------------------------------------------------------------------

export interface VersionChange {
  readonly path: string;
  readonly from: string | null;
  readonly to: string | null;
}

const priceText = (m: Money | null | undefined): string | null => (m === null || m === undefined ? null : `${m.minor.toString()} ${m.currency}`);

/**
 * What publishing a draft over the current published version changes, path by path, for staff to confirm. The
 * published version is read only: the draft becomes the next version, and locations stay on theirs until moved.
 */
export function versionChanges(published: PlanTerms | null, draft: PlanTerms): VersionChange[] {
  const changes: VersionChange[] = [];
  const diff = (path: string, from: string | null, to: string | null): void => {
    if (from !== to) changes.push({ path, from, to });
  };
  diff('interval', published?.interval ?? null, draft.interval);
  diff('basePrice', priceText(published?.basePrice), priceText(draft.basePrice));
  const featureKeys = [...new Set([...Object.keys(published?.features ?? {}), ...Object.keys(draft.features)])].sort();
  for (const k of featureKeys) {
    const a = published?.features[k];
    const b = draft.features[k];
    diff(`features.${k}.inclusion`, a?.inclusion ?? null, b?.inclusion ?? null);
    diff(`features.${k}.price`, a?.inclusion === 'a_la_carte' ? priceText(a.price) : null, b?.inclusion === 'a_la_carte' ? priceText(b.price) : null);
  }
  for (const k of DEVICE_CLASS_KEYS) {
    const a = published?.devices[k];
    const b = draft.devices[k];
    diff(`devices.${k}.firstUnit`, priceText(a?.firstUnit), priceText(b?.firstUnit));
    diff(`devices.${k}.eachAdditional`, priceText(a?.eachAdditional), priceText(b?.eachAdditional));
  }
  return changes;
}

// ---- the version's billable lines (what Stripe is given) -------------------------------------------------------------

/** One recurring price a published version bills: flat (the base, an a la carte feature) or tiered (a device class). */
export type VersionLine =
  | { readonly lineKey: LineKey; readonly pricing: 'flat'; readonly amount: Money }
  | { readonly lineKey: LineKey; readonly pricing: 'tiered'; readonly firstUnit: Money; readonly eachAdditional: Money };

/**
 * The prices a published version needs in the billing processor: the base, each a la carte feature (included ones
 * cost nothing and are not sold), and each device class it prices, as graduated tiers (unit 1 at the first-unit
 * price, units 2 and up at each additional), so the processor prices any quantity as `deviceCharge` does. A $0 line
 * (kitchen printers) is still a price, so the invoice lists the units as included. Null for a version that can't be
 * published (a blank price): such a version never reaches the processor.
 */
export function versionLines(terms: PlanTerms, featureKeys: readonly string[]): VersionLine[] | null {
  if (publishProblems(terms, featureKeys).length > 0 || terms.basePrice === null) return null;
  const lines: VersionLine[] = [{ lineKey: 'base', pricing: 'flat', amount: terms.basePrice }];
  for (const key of Object.keys(terms.features).sort()) {
    const f = terms.features[key];
    if (f?.inclusion === 'a_la_carte' && f.price !== null) lines.push({ lineKey: `feature:${key}`, pricing: 'flat', amount: f.price });
  }
  for (const key of DEVICE_CLASS_KEYS) {
    const d = terms.devices[key];
    if (d === undefined || d.firstUnit === null || d.eachAdditional === null) continue;
    lines.push({ lineKey: `device:${key}`, pricing: 'tiered', firstUnit: d.firstUnit, eachAdditional: d.eachAdditional });
  }
  return lines;
}

// ---- the subscription's items (what the processor bills each month) -------------------------------------------------

/** One item of a location's subscription: a version line and how many units of it. */
export interface SubscriptionItem {
  readonly lineKey: LineKey;
  readonly quantity: number;
}

/**
 * The items a location's subscription holds: the base (one), each a la carte feature bought (one each), and each device
 * class the version prices with at least one unit counted (its quantity; the processor's graduated tiers price it as
 * `deviceCharge` does). Included features cost nothing and are not items. A device class with no units is left out, so
 * the invoice lists only what the location has; it comes back as an item when a unit is counted.
 */
export function subscriptionItems(terms: PlanTerms, purchased: readonly string[], quantities: Partial<Record<DeviceClassKey, number>>): SubscriptionItem[] {
  const items: SubscriptionItem[] = [{ lineKey: 'base', quantity: 1 }];
  for (const key of [...new Set(purchased)].sort()) {
    if (terms.features[key]?.inclusion === 'a_la_carte') items.push({ lineKey: `feature:${key}`, quantity: 1 });
  }
  for (const key of DEVICE_CLASS_KEYS) {
    const units = quantities[key] ?? 0;
    if (terms.devices[key] !== undefined && units > 0) items.push({ lineKey: `device:${key}`, quantity: units });
  }
  return items;
}

export type ItemChange =
  | { readonly change: 'add'; readonly lineKey: LineKey; readonly quantity: number }
  | { readonly change: 'set_quantity'; readonly lineKey: LineKey; readonly quantity: number }
  | { readonly change: 'remove'; readonly lineKey: LineKey };

/**
 * What changes turn the items a subscription holds into the ones it should hold, line by line. Repeating it on the
 * result gives nothing: the quantity reconcile is idempotent.
 */
export function itemChanges(current: readonly SubscriptionItem[], wanted: readonly SubscriptionItem[]): ItemChange[] {
  const now = new Map(current.map((i) => [i.lineKey, i.quantity]));
  const changes: ItemChange[] = [];
  for (const w of wanted) {
    const q = now.get(w.lineKey);
    if (q === undefined) changes.push({ change: 'add', lineKey: w.lineKey, quantity: w.quantity });
    else if (q !== w.quantity) changes.push({ change: 'set_quantity', lineKey: w.lineKey, quantity: w.quantity });
  }
  for (const c of current) if (!wanted.some((w) => w.lineKey === c.lineKey)) changes.push({ change: 'remove', lineKey: c.lineKey });
  return changes;
}

/**
 * Whether a location's free time still runs today: staff may then put it back on Legacy with nothing charged (plan
 * §4.6 step 3). Once the first billing date has come, it is cancelled instead.
 */
export const mayRevert = (adjustments: readonly Adjustment[], today: LocalDate): boolean => freeUntil(adjustments, today) !== null;

// ---- Not yet on a plan (§4.6 step 1, IMPROVE L440) --------------------------------------------------------------------

/** A location staff still bill by hand (no live subscription: it reads "Legacy, everything included"). */
export interface NotYetOnPlanRow {
  readonly locationName: string;
  /** The location's date it moved to Radish Next (its device switch-over), or null when it never migrated. */
  readonly migratedOn: LocalDate | null;
  /** The day its manual billing is paid up to, set by staff, or null. */
  readonly paidByHandUntil: LocalDate | null;
}

/**
 * `due`: the date paid by hand has come (on or before today), so the store should be on a plan by now; the row turns
 * amber. Nothing else happens when a date passes: the store keeps every feature until staff move it (Decided 5).
 * `waiting`: a date is set and still ahead. `no_date`: none is set.
 */
export type NotYetOnPlanStanding = 'due' | 'waiting' | 'no_date';

export function notYetOnPlanStanding(paidByHandUntil: LocalDate | null, today: LocalDate): NotYetOnPlanStanding {
  if (paidByHandUntil === null) return 'no_date';
  return paidByHandUntil <= today ? 'due' : 'waiting';
}

/** Whole days on Legacy since the switch-over (0 on the day itself), or null when the location never migrated. */
export function daysOnLegacy(migratedOn: LocalDate | null, today: LocalDate): number | null {
  if (migratedOn === null) return null;
  return Math.max(0, localDateToDays(today) - localDateToDays(migratedOn));
}

/**
 * The list's order: rows with a paid-until date first, earliest date first (so the overdue ones lead); then the rest,
 * longest on Legacy first (a location that never migrated after every migrated one); then by name.
 */
export function compareNotYetOnPlan(a: NotYetOnPlanRow, b: NotYetOnPlanRow): number {
  if (a.paidByHandUntil !== b.paidByHandUntil) {
    if (a.paidByHandUntil === null) return 1;
    if (b.paidByHandUntil === null) return -1;
    return a.paidByHandUntil < b.paidByHandUntil ? -1 : 1;
  }
  if (a.migratedOn !== b.migratedOn) {
    if (a.migratedOn === null) return 1;
    if (b.migratedOn === null) return -1;
    return a.migratedOn < b.migratedOn ? -1 : 1;
  }
  return a.locationName.localeCompare(b.locationName, 'en', { sensitivity: 'base' });
}
